import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { Field, FieldArrayRenderProps } from 'formik';
import { Button } from 'react-bootstrap';
import { CommonHash } from 'app/types';
import 'react-datepicker/dist/react-datepicker.css';

import Text from 'common/components/Text/Text';
import styles from './LandingForm.module.scss';
import { LANDING_BACKGROUND_OPTIONS, LANDING_COMPONENTS } from 'common/constants';
import HeroBannerForm from './ComponentTypesForms/HeroBannerForm';
import MarkdownForm from './ComponentTypesForms/MarkdownForm';
import YoutubeVideoForm from './ComponentTypesForms/YoutubeVideoForm';
import BannerForm from './ComponentTypesForms/BannerForm';
import CardGridForm from './ComponentTypesForms/CardGridForm';
import FAQSectionForm from './ComponentTypesForms/FAQSectionForm';
import WebinarSectionForm from './ComponentTypesForms/WebinarSectionForm';
import ContentGridForm from './ComponentTypesForms/ContentGridForm';
import NewsletterBannerForm from './ComponentTypesForms/NewsletterBannerForm';
import TestimonialSectionForm from './ComponentTypesForms/TestimonialSectionForm';
import { TitleComponentForm } from './ComponentTypesForms/TitleComponentForm';
import GenericFormForm from './ComponentTypesForms/GenericFormForm';

interface LandingComponentFormProps {
  index: number;
  landing_cmp: CommonHash;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void;
  handleChange: {
    (e: React.ChangeEvent<any>): void;
    <T = string | React.ChangeEvent<any>>(field: T): T extends React.ChangeEvent<any>
      ? void
      : (e: string | React.ChangeEvent<any>) => void;
  };
  arrayHelpers: FieldArrayRenderProps;
  rendererKey: number;
  setRendererKey: (key: number) => void;
}

const LandingComponentForm = ({
  index,
  landing_cmp,
  setFieldValue,
  handleChange,
  arrayHelpers,
  rendererKey,
  setRendererKey,
}: LandingComponentFormProps): React.ReactElement => {
  const backgroundImageOptions = LANDING_BACKGROUND_OPTIONS.map(([label, value]) => ({
    label: label,
    value: value,
  }));
  const componentTypeOptions = LANDING_COMPONENTS.map(([label, value]) => ({
    label: label,
    value: value,
  }));

  const buildComponentConfig = (componentType: string) => {
    if (!landing_cmp.config) return;
    if (componentType === 'hero_banner') {
      landing_cmp.config = {
        title: '',
        style: 'normal',
        alignment: 'left',
        section_title_color: 'default',
        badge_color: 'primary',
        title_type: 'default',
        title_size: 'default',
        title_color: 'default',
        description_size: 'default',
        description_color: 'default',
      };
    } else if (componentType === 'markdown') {
      landing_cmp.config = {
        title: '',
        title_color: '#193676',
        title_size: 'normal',
      };
    } else if (componentType === 'youtube_video') {
      landing_cmp.config = {
        title: '',
        title_color: '#193676',
        title_size: 'normal',
      };
    } else if (componentType === 'banner') {
      landing_cmp.config = {
        title: '',
      };
    } else if (componentType === 'card_grid') {
      landing_cmp.config = {
        title: '',
        title_color: '#193676',
        title_size: 'normal',
        card_style: 'simple',
        display_overflow: 'downwards',
        columns_lg: '4',
        columns_md: '2',
        columns_sm: '2-2',
      };
    } else if (componentType === 'faq') {
      landing_cmp.config = {
        title: '',
        title_color: '#193676',
        title_size: 'normal',
      };
    } else if (componentType === 'webinar') {
      landing_cmp.config = {
        title: '',
        title_color: '#193676',
        title_size: 'normal',
      };
    } else if (componentType === 'title') {
      landing_cmp.config = {
        title: '',
      };
    } else if (componentType === 'form') {
      landing_cmp.config = {
        heading: '',
        sub_heading: '',
        associated_list: '',
        redirect_url: '',
        button_text: '',
      };
    }
  };

  const buildSubComponentConfig = (componentType: string) => {
    if (componentType === 'card_grid') {
      landing_cmp.sub_landing_components = [
        {
          config: {
            icon_name: '',
            icon_style: 'on_top',
            card_title: '',
            card_text: '',
            card_background: 'transparent',
          },
        },
      ];
    } else if (componentType === 'faq') {
      landing_cmp.sub_landing_components = [
        {
          config: {
            faq_question: '',
            faq_answer: '',
          },
        },
      ];
    } else if (componentType === 'webinar') {
      landing_cmp.sub_landing_components = [
        {
          config: {
            webinar_title: '',
            webinar_date: '',
            webinar_timezone: '',
            webinar_description: '',
            webinar_link: '',
          },
        },
      ];
    } else if (componentType === 'content_grid') {
      landing_cmp.sub_landing_components = [
        {
          config: {
            tab_name: '',
            tab_type: 'subjects',
            tab_style: 'standard',
            button_style: 'primary',
          },
        },
      ];
    }
  };

  const buildConfig = (componentType: string) => {
    buildComponentConfig(componentType);
    buildSubComponentConfig(componentType);
    landing_cmp.landing_component_buttons = [
      {
        text: '',
        link: '',
        style: 'primary',
        size: 'default',
      },
    ];
  };

  const labelOfBackground = (background: string) => {
    if (background == 'image') {
      return 'Upload image';
    }
    const label = backgroundImageOptions.find((option) => option.value === background)?.label;
    if (label) {
      return label;
    } else {
      return 'Select Custom';
    }
  };

  useEffect(() => console.log('')), [landing_cmp];
  return (
    <>
      <div className="d-flex gap-3 align-items-center mb-2">
        <Text size={22}> Landing Component {index + 1} </Text>
        <Button
          type="button"
          className={styles.deleteButton}
          onClick={() => {
            setFieldValue(`landing_components[${index}]._destroy`, true);
          }}
        >
          Delete
        </Button>
      </div>
      <div className={styles.group}>
        <label htmlFor={`landing_components[${index}].html_id`}>HTML ID</label>
        <input
          name={`landing_components[${index}].html_id`}
          placeholder="HTML ID"
          onChange={handleChange}
          // defaultValue={landing_cmp.html_id || ''}
          value={landing_cmp.html_id || ''}
          required={
            landing_cmp.componentType === 'html_inner_script' ||
            landing_cmp.componentType === 'html_outer_script'
              ? true
              : false
          }
        />
      </div>
      <div className={styles.group}>
        <label htmlFor={`landing_components[${index}].class_name`}>Class Names</label>
        <input
          name={`landing_components[${index}].class_name`}
          placeholder="Class Names"
          onChange={handleChange}
          value={landing_cmp.class_name || ''}
        />
      </div>
      <div className={styles.group}>
        <label htmlFor={`landing_components[${index}].size`}>Size</label>
        <select
          name={`landing_components[${index}].size`}
          onChange={handleChange}
          value={landing_cmp.size}
        >
          <option value="lg">Large</option>
          <option value="md">Medium</option>
          <option value="sm">Small</option>
        </select>
      </div>
      <div className={styles.group}>
        <label htmlFor={`landing_components[${index}].gap`}>Gap</label>
        <select
          name={`landing_components[${index}].gap`}
          onChange={handleChange}
          value={landing_cmp.gap}
        >
          <option value="lg">Large</option>
          <option value="md">Medium</option>
          <option value="sm">Small</option>
          <option value="none">None</option>
        </select>
      </div>
      <div className={styles.group}>
        <label htmlFor={`landing_components[${index}].background_size`}>Background size</label>
        <select
          name={`landing_components[${index}].background_size`}
          onChange={handleChange}
          value={landing_cmp.background_size}
        >
          <option value="contained">Contained</option>
          <option value="full_width">Full width</option>
        </select>
      </div>
      <div className={styles.group}>
        <div className="d-flex align-items-center gap-2 mb-1">
          <label htmlFor={`landing_components[${index}].background`}>Background</label>
          {landing_cmp.background === 'image' && (
            <i
              className="bi bi-trash"
              onClick={() => {
                setFieldValue(`landing_components[${index}].background`, '');
                setFieldValue(`landing_components[${index}].background_image`, '');
              }}
            />
          )}
        </div>
        <div className="d-flex align-items-center gap-2 mb-3">
          <Select
            isSearchable
            classNamePrefix="react-select"
            className={styles.filtersFormSelect}
            placeholder="Select Background"
            options={backgroundImageOptions}
            onChange={(background: any) => {
              handleChange({
                target: {
                  name: `landing_components[${index}].background`,
                  value: background.value,
                },
              });
              handleChange({
                target: {
                  name: `landing_components[${index}].background_image`,
                  value: '',
                },
              });
            }}
            value={{
              label: labelOfBackground(landing_cmp.background),
              value: landing_cmp.background,
            }}
          />
        </div>
        {landing_cmp.background === 'image' && (
          <>
            {landing_cmp.background_image && !(landing_cmp.background_image instanceof File) && (
              <div className="d-flex align-items-center gap-2">
                {landing_cmp.background_image.split('/').pop()}
                <i
                  className="bi bi-download"
                  onClick={() => window.open(landing_cmp.background_image)}
                />
              </div>
            )}
            {landing_cmp.background_image && (
              <img
                src={
                  landing_cmp.background_image instanceof File
                    ? URL.createObjectURL(landing_cmp.background_image)
                    : landing_cmp.background_image
                }
                className={`my-2 ${styles.image}`}
              />
            )}

            <Field
              type="file"
              name={`landing_components[${index}].background_ie`}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const files = event.currentTarget.files;
                if (files !== null) {
                  handleChange({
                    target: {
                      name: `landing_components[${index}].background_image`,
                      value: files[0],
                    },
                  });
                }
              }}
            />
          </>
        )}
        {!['white', 'lightblue', 'lighyellow', 'darkblue', 'image'].includes(
          landing_cmp.background
        ) && (
          <>
            <label htmlFor={`landing_components[${index}].background`}>Background color</label>
            <input
              name={`landing_components[${index}].background`}
              placeholder="Background color"
              onChange={handleChange}
              value={landing_cmp.background || ''}
            />
          </>
        )}
      </div>
      <div className={styles.group}>
        <label htmlFor={`landing_components[${index}].component_type`}>Component Type</label>
        <div className={styles.selectorAttribute}>
          <Select
            isSearchable
            classNamePrefix="react-select"
            className={styles.filtersFormSelect}
            placeholder="Select Component type"
            options={componentTypeOptions}
            onChange={(componentType: any) => {
              buildConfig(componentType.value);
              handleChange({
                target: {
                  name: `landing_components[${index}].component_type`,
                  value: componentType.value,
                },
              });
            }}
            value={{
              label: componentTypeOptions.find(
                (option) => option.value === landing_cmp.component_type
              )?.label,
              value: landing_cmp.component_type,
            }}
          />
        </div>
      </div>
      {landing_cmp.component_type === 'hero_banner' && (
        <HeroBannerForm landing_cmp={landing_cmp} handleChange={handleChange} index={index} />
      )}
      {landing_cmp.component_type === 'markdown' && (
        <MarkdownForm landing_cmp={landing_cmp} handleChange={handleChange} index={index} />
      )}
      {landing_cmp.component_type === 'youtube_video' && (
        <YoutubeVideoForm landing_cmp={landing_cmp} handleChange={handleChange} index={index} />
      )}
      {landing_cmp.component_type === 'banner' && (
        <BannerForm landing_cmp={landing_cmp} handleChange={handleChange} index={index} />
      )}
      {landing_cmp.component_type === 'card_grid' && (
        <CardGridForm landing_cmp={landing_cmp} handleChange={handleChange} index={index} />
      )}
      {landing_cmp.component_type === 'faq' && (
        <FAQSectionForm landing_cmp={landing_cmp} handleChange={handleChange} index={index} />
      )}
      {landing_cmp.component_type === 'webinar' && (
        <WebinarSectionForm landing_cmp={landing_cmp} handleChange={handleChange} index={index} />
      )}
      {landing_cmp.component_type === 'content_grid' && (
        <ContentGridForm landing_cmp={landing_cmp} handleChange={handleChange} index={index} />
      )}
      {landing_cmp.component_type === 'newsletter_banner' && (
        <NewsletterBannerForm
          landing_cmp={landing_cmp}
          handleChange={handleChange}
          setFieldValue={setFieldValue}
          index={index}
        />
      )}
      {landing_cmp.component_type === 'testimonial' && (
        <TestimonialSectionForm
          landing_cmp={landing_cmp}
          handleChange={handleChange}
          index={index}
        />
      )}
      {landing_cmp.component_type === 'html_outer_script' && (
        <div className={styles.group}>
          <label htmlFor={`landing_components[${index}].config['script']`}>Script</label>
          <input
            name={`landing_components[${index}].config['script']`}
            placeholder="Script"
            onChange={handleChange}
            defaultValue={landing_cmp.config.script || ''}
          />
        </div>
      )}
      {landing_cmp.component_type === 'html_inner_script' && (
        <div className={styles.group}>
          <label htmlFor={`landing_components[${index}].config['script']`}>Script</label>
          <input
            name={`landing_components[${index}].config['script']`}
            placeholder="Script"
            onChange={handleChange}
            defaultValue={landing_cmp.config.script || ''}
          />
        </div>
      )}
      {landing_cmp.component_type === 'title' && (
        <TitleComponentForm landing_cmp={landing_cmp} handleChange={handleChange} index={index} />
      )}
      {landing_cmp.component_type === 'form' && (
        <GenericFormForm landing_cmp={landing_cmp} handleChange={handleChange} index={index} />
      )}
      <Button
        type="button"
        variant="success"
        className="mt-3"
        onClick={() => {
          setRendererKey(rendererKey + 1);
          arrayHelpers.insert(index + 1, {
            html_id: '',
            class_name: '',
            size: 'md',
            gap: 'md',
            background_size: 'contained',
            background: 'white',
            title_color: '#193676',
            title_size: 'normal',
            alignment: 'right',
            component_type: 'hero_banner',
            style: 'normal',
            config: {
              title: '',
            },
            landing_component_buttons: [
              {
                text: '',
                link: '',
                style: 'primary',
              },
            ],
            sub_landing_components: [
              {
                config: {
                  icon_name: '',
                  icon_style: 'on_top',
                  card_title: '',
                  card_text: '',
                  card_background: 'transparent',
                },
              },
            ],
          });
        }}
      >
        + Landing Component
      </Button>
      <hr />
    </>
  );
};

export default LandingComponentForm;
